// ============================================================
// nextStep.ts — (Settlement redesign) where a settlement stands, worked out once:
// the plain-English sentence at the top, the step trail, and the ONE main button
// (always the same spot, its label changing as the case moves on). The screen
// only reads this; the server still decides what is allowed, and refuses anything
// this gets wrong.
// ============================================================
import type { CaseStatements, StatementDraft, StatementSummary } from '../../../types/settlementStatement.types'
import { dateInWords } from '../../../utils/civilDate'
import { isEsignInProgress, isFullySigned } from './EsignStatus'

export type MainAction = 'createCfa' | 'addFigures' | 'checkFigures' | 'start' | 'prepare' | 'approveIssue' | 'approve' | 'ask' | 'issue' | 'send' | 'answer' | 'change' | 'finalStatement' | 'later' | 'laterApprove' | 'none'
export interface Trail { label: string; done: boolean; now: boolean }
// What the case still needs before there is anything to state.
export type Setup = 'noCfa' | 'noDamages' | null
export interface NextStep {
  // The sentence: what stands, then the next step or who we're waiting for.
  stands: string
  next: string
  tag: string
  main: { action: MainAction; label: string } | null
  trail: Trail[]
}
export interface MoneyNow { netPence: number; deductionsPence: number; ok: boolean }
export const APPROVE_AND_ISSUE = 'Approve and issue'
// The sentence's money: whole pounds, pence only when there are some. (The money
// summary and the statements always show the pence.)
export const pounds = (pence: number): string => `£${(pence / 100).toLocaleString('en-GB', { minimumFractionDigits: pence % 100 ? 2 : 0, maximumFractionDigits: 2 })}`
// Never two amounts either side of a full stop; no deductions said in words; a
// sentence never starts with an amount or a number; dates in words.
const adultSays = (who: string, verb: string, net: number, ded: number): string => (ded > 0 ? `${who} ${verb} ${pounds(net)}, with ${pounds(ded)} deducted.` : `${who} ${verb} ${pounds(net)}. Nothing is deducted.`)
const childSays = (net: number, place: string, who: string, ded: number): string => `${who}’s ${pounds(net)} would go ${place}${ded > 0 ? `, with ${pounds(ded)} proposed as a deduction.` : '. No deduction is proposed.'}`
const listNames = (xs: string[]): string => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} or ${xs[xs.length - 1]}`)
const day = (iso: string | Date | null | undefined): string => (iso ? dateInWords(String(iso instanceof Date ? iso.toISOString() : iso).slice(0, 10)) : '')
const daysAgo = (iso: string | null | undefined, today: string): string => {
  if (!iso) return ''
  const n = Math.round((Date.parse(today) - Date.parse(String(iso).slice(0, 10))) / 86_400_000)
  return n <= 0 ? 'today' : n === 1 ? 'yesterday' : `${n} days ago`
}

// Who can take it all the way alone: an approver, with nobody else needed.
export const canDoItAll = (d: Pick<CaseStatements, 'canCheck' | 'onlyApprover' | 'twoPersonChecks'>): boolean =>
  d.canCheck && (d.twoPersonChecks === false || d.onlyApprover)

// The blockers that are real work: "prepare" and "approve" are the main button itself.
const isOwnStep = (t: string): boolean => t === 'prepare' || t === 'approve' || t === 'check'
export const realBlockers = (d: Pick<StatementDraft, 'blockers'>): StatementDraft['blockers'] => d.blockers.filter((b) => !isOwnStep(b.fix.target))
// Either date missing: both are added together, in one small editor. There is no
// default settlement date, and a draft can't be saved without one, so with no
// statement yet this is what creates it.
export const ADD_THE_DATES = 'Add the dates'
export const datesMissing = (d: Pick<StatementDraft, 'settlementDate' | 'blockers' | 'prepared' | 'checked'>): boolean =>
  !d.prepared && !d.checked && (!d.settlementDate || d.blockers.some((b) => b.code === 'NO_DAMAGES_RECEIVED_DATE'))
export const notesOutOfDate = (d: Pick<StatementDraft, 'blockers'>): boolean => d.blockers.some((b) => b.code === 'NOTES_NOT_REVIEWED')
export const issueHeldByBill = (d: Pick<StatementDraft, 'billMismatch' | 'billMismatchIssueProblem' | 'billMismatchOverridden'>): string | null =>
  (d.billMismatch ? (d.billMismatchIssueProblem ?? (d.billMismatchOverridden ? null : 'An approver needs to OK the difference with the final bill first.')) : null)

// Send: e-signature only where the firm's pilot is on and this statement can be
// signed (a PROPOSED statement has no agreement block); otherwise email. Post or
// by hand is one click away in the window either way.
export const sendDefault = (d: Pick<CaseStatements, 'esignPilot'>, s: Pick<StatementSummary, 'proposed'>): 'esign' | undefined =>
  (d.esignPilot && !s.proposed ? 'esign' : undefined)

// The client's answer form starts from how the statement went out.
export const answerMethodFor = (delivered: StatementSummary['delivered']): 'EMAIL_REPLY' | 'SIGNED_FORM' | 'IN_PERSON' | '' =>
  (delivered?.method === 'EMAIL' ? 'EMAIL_REPLY' : delivered?.method === 'POST' ? 'SIGNED_FORM' : delivered?.method === 'HAND' ? 'IN_PERSON' : '')

const trailOf = (child: boolean, step: number): Trail[] => ['Prepared', 'Approved', 'Issued', 'Sent', child ? 'Court' : 'Answer']
  .map((label, i) => ({ label, done: i < step, now: i === step }))
// Nothing to state yet: the real next step, whatever the screen is showing.
const setupStep = (setup: NonNullable<Setup>, tag: string, trail: Trail[]): NextStep => (setup === 'noCfa'
  ? { stands: '', next: 'Next: create the CFA.', tag, trail, main: { action: 'createCfa', label: 'Create the CFA from firm defaults' } }
  : { stands: '', next: 'Next: add the damages and costs.', tag, trail, main: { action: 'addFigures', label: 'Add the figures' } })

export interface NextStepFacts {
  data: CaseStatements
  // The client's name for the sentence ("Travis"), or null.
  name: string | null
  // The live figures for a draft; an issued statement's own frozen ones are used once issued.
  figures: MoneyNow | null
  setup?: Setup
}

const draftStep = (draft: StatementDraft, f: NextStepFacts): NextStep => {
  const { data, name, figures } = f
  const who = name ?? 'The client'
  const child = draft.capacity !== 'ADULT'
  if (f.setup && !draft.prepared && !draft.checked) return setupStep(f.setup, draft.proposed ? 'Draft — proposed' : 'Draft — not for client use', trailOf(child, 0))
  const net = figures?.ok ? figures.netPence : draft.figures.netToClientPence
  const ded = figures?.ok ? figures.deductionsPence : draft.figures.totalDeductionsPence
  const stands = child ? childSays(net, draft.netDestination === 'COURT_FUNDS_OFFICE' ? 'to the Court Funds Office' : 'to the account chosen', who, ded) : adultSays(who, 'would get', net, ded)
  const prepared = !!draft.prepared
  const checked = !!draft.checked
  const trail = trailOf(child, checked ? 2 : prepared ? 1 : 0)
  const blockers = realBlockers(draft)
  const firstBlocker = blockers[0]?.fix.label.replace(/ \(\d+\)$/, '')
  const issueLabel = draft.proposed ? 'Approve and issue as proposed' : APPROVE_AND_ISSUE
  const tag = checked ? 'Approved' : prepared ? 'Prepared' : draft.proposed ? 'Draft — proposed' : 'Draft — not for client use'
  const canFinish = blockers.length === 0 && !issueHeldByBill(draft)

  if (checked && notesOutOfDate(draft)) {
    // New notes since it was approved: it needs approving again, with the tick.
    const a = draft.approval
    if (!(a ? a.allowed : data.canCheck)) return { stands, next: 'Waiting for someone who can approve it again.', tag, trail, main: null }
    return { stands, next: 'Next: review the new notes and approve it again.', tag, trail, main: { action: 'approve', label: 'Approve' } }
  }
  if (checked) {
    return { stands, next: blockers.length ? `Next: ${firstBlocker?.toLowerCase() ?? 'clear what’s listed'}.` : 'Next: issue the statement.', tag, trail, main: { action: 'issue', label: 'Issue statement' } }
  }
  if (prepared) {
    const a = draft.approval
    if (a?.needsColleague) {
      const names = a.askable.length ? listNames(a.askable) : 'a colleague'
      return { stands, next: `Waiting for ${names} to approve.`, tag, trail, main: a.askable.length ? { action: 'ask', label: `Ask ${names} to approve` } : null }
    }
    if (!(a ? a.allowed : data.canCheck)) return { stands, next: 'Waiting for someone who can approve it.', tag, trail, main: null }
    return { stands, next: 'Next: check it, then approve and issue.', tag, trail, main: canFinish ? { action: 'approveIssue', label: issueLabel } : { action: 'approve', label: 'Approve' } }
  }
  if (datesMissing(draft)) return { stands, next: 'Next: add the dates.', tag, trail, main: canDoItAll(data) ? { action: 'approveIssue', label: issueLabel } : { action: 'prepare', label: 'Mark as prepared' } }
  if (firstBlocker) return { stands, next: `Next: ${firstBlocker.toLowerCase()}.`, tag, trail, main: canDoItAll(data) ? { action: 'approveIssue', label: issueLabel } : { action: 'prepare', label: 'Mark as prepared' } }
  if (canDoItAll(data)) return { stands, next: draft.proposed ? 'Next: issue the proposed statement for the hearing.' : 'Next: check the figures, then approve and issue.', tag, trail, main: canFinish ? { action: 'approveIssue', label: issueLabel } : { action: 'approve', label: 'Approve' } }
  return { stands, next: 'Next: check the figures and mark it as prepared.', tag, trail, main: { action: 'prepare', label: 'Mark as prepared' } }
}

const issuedStep = (s: StatementSummary, f: NextStepFacts): NextStep => {
  const { data, name } = f
  const who = name ?? 'the client'
  const Who = name ?? 'The client'
  const child = s.capacity !== 'ADULT'
  const net = s.figures.netToClientPence
  const ded = s.figures.totalDeductionsPence
  const ref = `${s.number ?? 'Statement'} v${s.version}`
  const esign = data.esign?.[s.id]
  const live = isEsignInProgress(esign)
  const sentOn = esign?.sentAt ?? (s.delivered?.at as unknown as string | undefined)
  const tag = `${s.proposed ? 'Proposed' : 'Issued'} · ${ref}`

  if (child) {
    const place = s.netDestination === 'COURT_FUNDS_OFFICE' ? 'to the Court Funds Office' : 'to the account chosen'
    if (s.proposed) {
      if (!s.delivered && !live) return { stands: childSays(net, place, Who, ded), next: 'Next: send the proposed statement to the litigation friend.', tag, trail: trailOf(true, 3), main: { action: 'send', label: 'Send to client' } }
      return { stands: childSays(net, place, Who, ded), next: 'Waiting for the court order. Record it once it’s made.', tag, trail: trailOf(true, 4), main: { action: 'finalStatement', label: 'Record the court order' } }
    }
    if (!s.delivered && !live) return { stands: `${Who}’s ${pounds(net)} goes ${place}.`, next: 'Next: send the statement to the litigation friend.', tag, trail: trailOf(true, 3), main: { action: 'send', label: 'Send to client' } }
    return s.readiness.ready
      ? { stands: `${Who}’s ${pounds(net)} is ready to go ${place}.`, next: 'Next: pay it from your accounts system.', tag, trail: trailOf(true, 5), main: null }
      : { stands: s.readiness.label, next: s.readiness.reason ? `Next: ${s.readiness.reason}` : 'Next: record the court steps as they happen.', tag, trail: trailOf(true, 4), main: null }
  }

  const a = s.clientResponse
  const pending = (s.answerHistory ?? []).find((e) => e.decision === 'PENDING')
  if (pending) {
    const decide = data.canCheck && (pending.recordedByUserId !== data.viewerUserId || data.onlyApprover || data.twoPersonChecks === false)
    return { stands: `${Who}’s later instruction is waiting for approval.`, next: decide ? 'Next: check it, then approve or refuse it.' : 'Waiting for an approver.', tag, trail: trailOf(false, 4), main: decide ? { action: 'laterApprove', label: 'Approve' } : null }
  }
  // A correction: the client's e-signed dispute on the last version still stands.
  const carried = s.carriedDispute
  if (carried && !a) {
    const heldNow = Math.min(carried.disputedPence, ded)
    const stands = `${Who}’s dispute from version ${carried.fromVersion} is still open — ${pounds(heldNow)} is held.`
    const tagged = 'Issued · open dispute'
    if (live) return { stands, next: `Waiting for ${who} to sign — sent ${daysAgo(sentOn, data.today)}.`, tag: tagged, trail: trailOf(false, 4), main: null }
    if (!s.delivered) return { stands, next: `Next: send the corrected statement to ${who}.`, tag: tagged, trail: trailOf(false, 3), main: { action: 'send', label: 'Send to client' } }
    return { stands, next: 'Next: record a later instruction, or send a fresh e-signature.', tag: tagged, trail: trailOf(false, 4), main: { action: 'later', label: 'Record a later instruction' } }
  }
  if (a?.response === 'DISPUTED') {
    const held = s.position?.heldPence ?? Math.min(a.disputedPence ?? 0, ded)
    return { stands: `${Who} disputed ${pounds(a.disputedPence ?? 0)} — ${pounds(held)} is held.`, next: 'Next: sort out the dispute.', tag: `Issued · disputed`, trail: trailOf(false, 4), main: a.esignLocked ? { action: 'later', label: 'Record a later instruction' } : { action: 'change', label: 'Change answer' } }
  }
  if (a?.response === 'AGREED') {
    const verb = a.method === 'E_SIGNATURE' ? 'agreed and signed' : 'agreed'
    return s.readiness.ready
      ? { stands: `${Who} ${verb} on ${day(a.date)}, so ${pounds(s.position?.payableToClientPence ?? net)} is ready for client account.`, next: 'Next: pay it from your accounts system.', tag, trail: trailOf(false, 5), main: null }
      : { stands: `${Who} ${verb} on ${day(a.date)}.`, next: s.readiness.reason ? `Next: ${s.readiness.reason}` : 'Next: nothing more is needed here.', tag, trail: trailOf(false, 5), main: null }
  }
  if (isFullySigned(esign)) return { stands: `${Who} has signed.`, next: 'Waiting for the signed copy to be sealed.', tag, trail: trailOf(false, 4), main: null }
  if (live) return { stands: adultSays(Who, 'gets', net, ded), next: `Waiting for ${who} to sign — sent ${daysAgo(sentOn, data.today)}.`, tag, trail: trailOf(false, 4), main: null }
  if (s.delivered) return { stands: adultSays(Who, 'gets', net, ded), next: `Waiting for ${who}’s answer — sent ${daysAgo(sentOn, data.today)}.`, tag, trail: trailOf(false, 4), main: { action: 'answer', label: 'Record the answer' } }
  return { stands: adultSays(Who, 'gets', net, ded), next: `Next: send the statement to ${who}.`, tag, trail: trailOf(false, 3), main: { action: 'send', label: 'Send to client' } }
}

export const nextStepOf = (f: NextStepFacts): NextStep => {
  const { data, figures, name } = f
  if (data.draft) return draftStep(data.draft, f)
  const current = data.statements.find((s) => s.status === 'ISSUED' || s.status === 'APPROVED') ?? null
  if (current) return issuedStep(current, f)
  const tag = 'Draft — not for client use'
  if (f.setup) return setupStep(f.setup, tag, trailOf(false, 0))
  const who = name ?? 'The client'
  return figures?.ok
    ? { stands: adultSays(who, 'would get', figures.netPence, figures.deductionsPence), next: 'Next: start the statement.', tag, trail: trailOf(false, 0), main: { action: 'start', label: 'Start a statement' } }
    : { stands: `The figures aren’t ready yet, so there’s nothing for ${name ?? 'the client'} to see.`, next: 'Next: check the figures.', tag, trail: trailOf(false, 0), main: { action: 'checkFigures', label: 'Check the figures' } }
}
